// Statistics Knowledge Graph Samples - Part 2
// Organized by difficulty levels with systematic naming

export const statisticsSamplesPart2 = {
  harder: [
    {
      id: 'stats_hard_01',
      problem: "Find the mean, median, and standard deviation of the data set: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12",
      problemType: "statistics",
      difficulty: "harder",
      description: "Statistics with 12 values",
      graphData: {
        nodes: [
          {
            id: "statistics",
            name: "Statistics",
            type: "concepts",
            description: "Collection, analysis, and interpretation of data",
            value: "Data analysis and interpretation",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "central_tendency",
            name: "Central Tendency",
            type: "concepts",
            description: "Measures that describe the center of a data set",
            value: "Mean, median, mode",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "variability",
            name: "Variability",
            type: "concepts",
            description: "Measures that describe the spread of data",
            value: "Variance, standard deviation, range",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "data_set",
            name: "Data Set",
            type: "values",
            description: "The given data values",
            value: "1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "mean_formula",
            name: "Mean Formula",
            type: "formulas",
            description: "Mean = Sum of all values ÷ Number of values",
            value: "Mean = (1+2+...+12) ÷ 12",
            importance: 10,
            x: -150, y: 0, z: 25
          },
          {
            id: "median_formula",
            name: "Median Formula",
            type: "formulas",
            description: "Median = Average of two middle values for even number of data points",
            value: "Median = (6th value + 7th value) ÷ 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "standard_deviation_formula",
            name: "Standard Deviation Formula",
            type: "formulas",
            description: "σ = √(Σ(x - μ)²/n)",
            value: "σ = √(Σ(x - μ)²/n)",
            importance: 10,
            x: 150, y: 0, z: 25
          },
          {
            id: "mean_result",
            name: "Mean Result",
            type: "values",
            description: "The mean of the data set",
            value: "Mean = 6.5",
            importance: 9,
            x: -120, y: 150, z: -15
          },
          {
            id: "median_result",
            name: "Median Result",
            type: "values",
            description: "The median of the data set",
            value: "Median = 6.5",
            importance: 8,
            x: 0, y: 150, z: -15
          },
          {
            id: "standard_deviation_result",
            name: "Standard Deviation Result",
            type: "values",
            description: "The standard deviation of the data set",
            value: "σ = 3.45",
            importance: 9,
            x: 120, y: 150, z: -15
          },
          {
            id: "identify_data",
            name: "Identify Data",
            type: "steps",
            description: "Extract the data set from the problem",
            value: "Step 1: Data = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_mean",
            name: "Calculate Mean",
            type: "steps",
            description: "Find the average of all values",
            value: "Step 2: Mean = (1+2+...+12)/12 = 78/12 = 6.5",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "find_median",
            name: "Find Median",
            type: "steps",
            description: "Find the middle value(s)",
            value: "Step 3: Median = (6+7)/2 = 6.5",
            importance: 8,
            x: 0, y: 50, z: 40
          },
          {
            id: "calculate_std_dev",
            name: "Calculate Standard Deviation",
            type: "steps",
            description: "Find the standard deviation",
            value: "Step 4: σ = √(Σ(x-6.5)²/12) = 3.45",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "statistics", to: "central_tendency", type: "includes", strength: 9 },
          { from: "statistics", to: "variability", type: "includes", strength: 8 },
          { from: "central_tendency", to: "mean_formula", type: "uses", strength: 9 },
          { from: "central_tendency", to: "median_formula", type: "uses", strength: 8 },
          { from: "variability", to: "standard_deviation_formula", type: "uses", strength: 9 },
          { from: "identify_data", to: "calculate_mean", type: "enables", strength: 9 },
          { from: "calculate_mean", to: "find_median", type: "enables", strength: 8 },
          { from: "find_median", to: "calculate_std_dev", type: "enables", strength: 9 },
          { from: "data_set", to: "identify_data", type: "identified in", strength: 10 },
          { from: "calculate_mean", to: "mean_result", type: "produces", strength: 9 },
          { from: "find_median", to: "median_result", type: "produces", strength: 8 },
          { from: "calculate_std_dev", to: "standard_deviation_result", type: "produces", strength: 9 },
          { from: "mean_formula", to: "calculate_mean", type: "implemented in", strength: 9 },
          { from: "median_formula", to: "find_median", type: "implemented in", strength: 8 },
          { from: "standard_deviation_formula", to: "calculate_std_dev", type: "implemented in", strength: 9 }
        ]
      }
    },
    {
      id: 'stats_hard_02',
      problem: "Find the mean, median, and standard deviation of the data set: 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39",
      problemType: "statistics",
      difficulty: "harder",
      description: "Statistics with 13 values",
      graphData: {
        nodes: [
          {
            id: "statistics",
            name: "Statistics",
            type: "concepts",
            description: "Collection, analysis, and interpretation of data",
            value: "Data analysis and interpretation",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "central_tendency",
            name: "Central Tendency",
            type: "concepts",
            description: "Measures that describe the center of a data set",
            value: "Mean, median, mode",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "variability",
            name: "Variability",
            type: "concepts",
            description: "Measures that describe the spread of data",
            value: "Variance, standard deviation, range",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "data_set",
            name: "Data Set",
            type: "values",
            description: "The given data values",
            value: "3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "mean_formula",
            name: "Mean Formula",
            type: "formulas",
            description: "Mean = Sum of all values ÷ Number of values",
            value: "Mean = (3+6+...+39) ÷ 13",
            importance: 10,
            x: -150, y: 0, z: 25
          },
          {
            id: "median_formula",
            name: "Median Formula",
            type: "formulas",
            description: "Median = Middle value for odd number of data points",
            value: "Median = 7th value",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "standard_deviation_formula",
            name: "Standard Deviation Formula",
            type: "formulas",
            description: "σ = √(Σ(x - μ)²/n)",
            value: "σ = √(Σ(x - μ)²/n)",
            importance: 10,
            x: 150, y: 0, z: 25
          },
          {
            id: "mean_result",
            name: "Mean Result",
            type: "values",
            description: "The mean of the data set",
            value: "Mean = 21",
            importance: 9,
            x: -120, y: 150, z: -15
          },
          {
            id: "median_result",
            name: "Median Result",
            type: "values",
            description: "The median of the data set",
            value: "Median = 21",
            importance: 8,
            x: 0, y: 150, z: -15
          },
          {
            id: "standard_deviation_result",
            name: "Standard Deviation Result",
            type: "values",
            description: "The standard deviation of the data set",
            value: "σ = 10.39",
            importance: 9,
            x: 120, y: 150, z: -15
          },
          {
            id: "identify_data",
            name: "Identify Data",
            type: "steps",
            description: "Extract the data set from the problem",
            value: "Step 1: Data = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39]",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_mean",
            name: "Calculate Mean",
            type: "steps",
            description: "Find the average of all values",
            value: "Step 2: Mean = (3+6+...+39)/13 = 273/13 = 21",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "find_median",
            name: "Find Median",
            type: "steps",
            description: "Find the middle value",
            value: "Step 3: Median = 21 (7th value)",
            importance: 8,
            x: 0, y: 50, z: 40
          },
          {
            id: "calculate_std_dev",
            name: "Calculate Standard Deviation",
            type: "steps",
            description: "Find the standard deviation",
            value: "Step 4: σ = √(Σ(x-21)²/13) = 10.39",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "statistics", to: "central_tendency", type: "includes", strength: 9 },
          { from: "statistics", to: "variability", type: "includes", strength: 8 },
          { from: "central_tendency", to: "mean_formula", type: "uses", strength: 9 },
          { from: "central_tendency", to: "median_formula", type: "uses", strength: 8 },
          { from: "variability", to: "standard_deviation_formula", type: "uses", strength: 9 },
          { from: "identify_data", to: "calculate_mean", type: "enables", strength: 9 },
          { from: "calculate_mean", to: "find_median", type: "enables", strength: 8 },
          { from: "find_median", to: "calculate_std_dev", type: "enables", strength: 9 },
          { from: "data_set", to: "identify_data", type: "identified in", strength: 10 },
          { from: "calculate_mean", to: "mean_result", type: "produces", strength: 9 },
          { from: "find_median", to: "median_result", type: "produces", strength: 8 },
          { from: "calculate_std_dev", to: "standard_deviation_result", type: "produces", strength: 9 },
          { from: "mean_formula", to: "calculate_mean", type: "implemented in", strength: 9 },
          { from: "median_formula", to: "find_median", type: "implemented in", strength: 8 },
          { from: "standard_deviation_formula", to: "calculate_std_dev", type: "implemented in", strength: 9 }
        ]
      }
    },
    {
      id: 'stats_hard_03',
      problem: "Find the mean, median, and standard deviation of the data set: 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70",
      problemType: "statistics",
      difficulty: "harder",
      description: "Statistics with 14 values",
      graphData: {
        nodes: [
          {
            id: "statistics",
            name: "Statistics",
            type: "concepts",
            description: "Collection, analysis, and interpretation of data",
            value: "Data analysis and interpretation",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "central_tendency",
            name: "Central Tendency",
            type: "concepts",
            description: "Measures that describe the center of a data set",
            value: "Mean, median, mode",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "variability",
            name: "Variability",
            type: "concepts",
            description: "Measures that describe the spread of data",
            value: "Variance, standard deviation, range",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "data_set",
            name: "Data Set",
            type: "values",
            description: "The given data values",
            value: "5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "mean_formula",
            name: "Mean Formula",
            type: "formulas",
            description: "Mean = Sum of all values ÷ Number of values",
            value: "Mean = (5+10+...+70) ÷ 14",
            importance: 10,
            x: -150, y: 0, z: 25
          },
          {
            id: "median_formula",
            name: "Median Formula",
            type: "formulas",
            description: "Median = Average of two middle values for even number of data points",
            value: "Median = (14th value + 15th value) ÷ 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "standard_deviation_formula",
            name: "Standard Deviation Formula",
            type: "formulas",
            description: "σ = √(Σ(x - μ)²/n)",
            value: "σ = √(Σ(x - μ)²/n)",
            importance: 10,
            x: 150, y: 0, z: 25
          },
          {
            id: "mean_result",
            name: "Mean Result",
            type: "values",
            description: "The mean of the data set",
            value: "Mean = 37.5",
            importance: 9,
            x: -120, y: 150, z: -15
          },
          {
            id: "median_result",
            name: "Median Result",
            type: "values",
            description: "The median of the data set",
            value: "Median = 37.5",
            importance: 8,
            x: 0, y: 150, z: -15
          },
          {
            id: "standard_deviation_result",
            name: "Standard Deviation Result",
            type: "values",
            description: "The standard deviation of the data set",
            value: "σ = 20.21",
            importance: 9,
            x: 120, y: 150, z: -15
          },
          {
            id: "identify_data",
            name: "Identify Data",
            type: "steps",
            description: "Extract the data set from the problem",
            value: "Step 1: Data = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70]",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_mean",
            name: "Calculate Mean",
            type: "steps",
            description: "Find the average of all values",
            value: "Step 2: Mean = (5+10+...+70)/14 = 525/14 = 37.5",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "find_median",
            name: "Find Median",
            type: "steps",
            description: "Find the middle value(s)",
            value: "Step 3: Median = (37.5 + 37.5) ÷ 2 = 37.5",
            importance: 8,
            x: 0, y: 50, z: 40
          },
          {
            id: "calculate_std_dev",
            name: "Calculate Standard Deviation",
            type: "steps",
            description: "Find the standard deviation",
            value: "Step 4: σ = √(Σ(x-37.5)²/14) = 20.21",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "statistics", to: "central_tendency", type: "includes", strength: 9 },
          { from: "statistics", to: "variability", type: "includes", strength: 8 },
          { from: "central_tendency", to: "mean_formula", type: "uses", strength: 9 },
          { from: "central_tendency", to: "median_formula", type: "uses", strength: 8 },
          { from: "variability", to: "standard_deviation_formula", type: "uses", strength: 9 },
          { from: "identify_data", to: "calculate_mean", type: "enables", strength: 9 },
          { from: "calculate_mean", to: "find_median", type: "enables", strength: 8 },
          { from: "find_median", to: "calculate_std_dev", type: "enables", strength: 9 },
          { from: "data_set", to: "identify_data", type: "identified in", strength: 10 },
          { from: "calculate_mean", to: "mean_result", type: "produces", strength: 9 },
          { from: "find_median", to: "median_result", type: "produces", strength: 8 },
          { from: "calculate_std_dev", to: "standard_deviation_result", type: "produces", strength: 9 },
          { from: "mean_formula", to: "calculate_mean", type: "implemented in", strength: 9 },
          { from: "median_formula", to: "find_median", type: "implemented in", strength: 8 },
          { from: "standard_deviation_formula", to: "calculate_std_dev", type: "implemented in", strength: 9 }
        ]
      }
    },
    {
      id: 'stats_hard_04',
      problem: "Find the mean, median, and standard deviation of the data set: 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30",
      problemType: "statistics",
      difficulty: "harder",
      description: "Statistics with 15 values",
      graphData: {
        nodes: [
          {
            id: "statistics",
            name: "Statistics",
            type: "concepts",
            description: "Collection, analysis, and interpretation of data",
            value: "Data analysis and interpretation",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "central_tendency",
            name: "Central Tendency",
            type: "concepts",
            description: "Measures that describe the center of a data set",
            value: "Mean, median, mode",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "variability",
            name: "Variability",
            type: "concepts",
            description: "Measures that describe the spread of data",
            value: "Variance, standard deviation, range",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "data_set",
            name: "Data Set",
            type: "values",
            description: "The given data values",
            value: "2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "mean_formula",
            name: "Mean Formula",
            type: "formulas",
            description: "Mean = Sum of all values ÷ Number of values",
            value: "Mean = (2+4+...+30) ÷ 15",
            importance: 10,
            x: -150, y: 0, z: 25
          },
          {
            id: "median_formula",
            name: "Median Formula",
            type: "formulas",
            description: "Median = Middle value for odd number of data points",
            value: "Median = 8th value",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "standard_deviation_formula",
            name: "Standard Deviation Formula",
            type: "formulas",
            description: "σ = √(Σ(x - μ)²/n)",
            value: "σ = √(Σ(x - μ)²/n)",
            importance: 10,
            x: 150, y: 0, z: 25
          },
          {
            id: "mean_result",
            name: "Mean Result",
            type: "values",
            description: "The mean of the data set",
            value: "Mean = 16",
            importance: 9,
            x: -120, y: 150, z: -15
          },
          {
            id: "median_result",
            name: "Median Result",
            type: "values",
            description: "The median of the data set",
            value: "Median = 16",
            importance: 8,
            x: 0, y: 150, z: -15
          },
          {
            id: "standard_deviation_result",
            name: "Standard Deviation Result",
            type: "values",
            description: "The standard deviation of the data set",
            value: "σ = 8.62",
            importance: 9,
            x: 120, y: 150, z: -15
          },
          {
            id: "identify_data",
            name: "Identify Data",
            type: "steps",
            description: "Extract the data set from the problem",
            value: "Step 1: Data = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30]",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_mean",
            name: "Calculate Mean",
            type: "steps",
            description: "Find the average of all values",
            value: "Step 2: Mean = (2+4+...+30)/15 = 240/15 = 16",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "find_median",
            name: "Find Median",
            type: "steps",
            description: "Find the middle value",
            value: "Step 3: Median = 16 (8th value)",
            importance: 8,
            x: 0, y: 50, z: 40
          },
          {
            id: "calculate_std_dev",
            name: "Calculate Standard Deviation",
            type: "steps",
            description: "Find the standard deviation",
            value: "Step 4: σ = √(Σ(x-16)²/15) = 8.62",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "statistics", to: "central_tendency", type: "includes", strength: 9 },
          { from: "statistics", to: "variability", type: "includes", strength: 8 },
          { from: "central_tendency", to: "mean_formula", type: "uses", strength: 9 },
          { from: "central_tendency", to: "median_formula", type: "uses", strength: 8 },
          { from: "variability", to: "standard_deviation_formula", type: "uses", strength: 9 },
          { from: "identify_data", to: "calculate_mean", type: "enables", strength: 9 },
          { from: "calculate_mean", to: "find_median", type: "enables", strength: 8 },
          { from: "find_median", to: "calculate_std_dev", type: "enables", strength: 9 },
          { from: "data_set", to: "identify_data", type: "identified in", strength: 10 },
          { from: "calculate_mean", to: "mean_result", type: "produces", strength: 9 },
          { from: "find_median", to: "median_result", type: "produces", strength: 8 },
          { from: "calculate_std_dev", to: "standard_deviation_result", type: "produces", strength: 9 },
          { from: "mean_formula", to: "calculate_mean", type: "implemented in", strength: 9 },
          { from: "median_formula", to: "find_median", type: "implemented in", strength: 8 },
          { from: "standard_deviation_formula", to: "calculate_std_dev", type: "implemented in", strength: 9 }
        ]
      }
    },
    {
      id: 'stats_hard_05',
      problem: "Find the mean, median, and standard deviation of the data set: 1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31",
      problemType: "statistics",
      difficulty: "harder",
      description: "Statistics with 16 values",
      graphData: {
        nodes: [
          {
            id: "statistics",
            name: "Statistics",
            type: "concepts",
            description: "Collection, analysis, and interpretation of data",
            value: "Data analysis and interpretation",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "central_tendency",
            name: "Central Tendency",
            type: "concepts",
            description: "Measures that describe the center of a data set",
            value: "Mean, median, mode",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "variability",
            name: "Variability",
            type: "concepts",
            description: "Measures that describe the spread of data",
            value: "Variance, standard deviation, range",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "data_set",
            name: "Data Set",
            type: "values",
            description: "The given data values",
            value: "1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "mean_formula",
            name: "Mean Formula",
            type: "formulas",
            description: "Mean = Sum of all values ÷ Number of values",
            value: "Mean = (1+3+...+31) ÷ 16",
            importance: 10,
            x: -150, y: 0, z: 25
          },
          {
            id: "median_formula",
            name: "Median Formula",
            type: "formulas",
            description: "Median = Average of two middle values for even number of data points",
            value: "Median = (16th value + 17th value) ÷ 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "standard_deviation_formula",
            name: "Standard Deviation Formula",
            type: "formulas",
            description: "σ = √(Σ(x - μ)²/n)",
            value: "σ = √(Σ(x - μ)²/n)",
            importance: 10,
            x: 150, y: 0, z: 25
          },
          {
            id: "mean_result",
            name: "Mean Result",
            type: "values",
            description: "The mean of the data set",
            value: "Mean = 16",
            importance: 9,
            x: -120, y: 150, z: -15
          },
          {
            id: "median_result",
            name: "Median Result",
            type: "values",
            description: "The median of the data set",
            value: "Median = 16",
            importance: 8,
            x: 0, y: 150, z: -15
          },
          {
            id: "standard_deviation_result",
            name: "Standard Deviation Result",
            type: "values",
            description: "The standard deviation of the data set",
            value: "σ = 9.24",
            importance: 9,
            x: 120, y: 150, z: -15
          },
          {
            id: "identify_data",
            name: "Identify Data",
            type: "steps",
            description: "Extract the data set from the problem",
            value: "Step 1: Data = [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31]",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "calculate_mean",
            name: "Calculate Mean",
            type: "steps",
            description: "Find the average of all values",
            value: "Step 2: Mean = (1+3+...+31)/16 = 272/16 = 16",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "find_median",
            name: "Find Median",
            type: "steps",
            description: "Find the middle value(s)",
            value: "Step 3: Median = (16 + 17) ÷ 2 = 16.5",
            importance: 8,
            x: 0, y: 50, z: 40
          },
          {
            id: "calculate_std_dev",
            name: "Calculate Standard Deviation",
            type: "steps",
            description: "Find the standard deviation",
            value: "Step 4: σ = √(Σ(x-16)²/16) = 9.24",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "statistics", to: "central_tendency", type: "includes", strength: 9 },
          { from: "statistics", to: "variability", type: "includes", strength: 8 },
          { from: "central_tendency", to: "mean_formula", type: "uses", strength: 9 },
          { from: "central_tendency", to: "median_formula", type: "uses", strength: 8 },
          { from: "variability", to: "standard_deviation_formula", type: "uses", strength: 9 },
          { from: "identify_data", to: "calculate_mean", type: "enables", strength: 9 },
          { from: "calculate_mean", to: "find_median", type: "enables", strength: 8 },
          { from: "find_median", to: "calculate_std_dev", type: "enables", strength: 9 },
          { from: "data_set", to: "identify_data", type: "identified in", strength: 10 },
          { from: "calculate_mean", to: "mean_result", type: "produces", strength: 9 },
          { from: "find_median", to: "median_result", type: "produces", strength: 8 },
          { from: "calculate_std_dev", to: "standard_deviation_result", type: "produces", strength: 9 },
          { from: "mean_formula", to: "calculate_mean", type: "implemented in", strength: 9 },
          { from: "median_formula", to: "find_median", type: "implemented in", strength: 8 },
          { from: "standard_deviation_formula", to: "calculate_std_dev", type: "implemented in", strength: 9 }
        ]
      }
    }
  ]
}; 